import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks/redux';
import { useCallback, useMemo, useRef, useState } from 'react';
import { setSelectedArray } from '@/widgets/ClubTable';
import { SelectedPopup } from '@/entities/SelectedPopup';
import { ClubTableItem } from '@/shared/model/types/table';
import { getClubTableColumns } from '@/widgets/ClubTable/model/columns';
import { Table } from '@/entities/Table';
import s from './ClubTable.module.scss';
import { Loader } from '@/shared/ui/Loader/Loader';
import { useGetClubsQuery, ClubItemApi, usePatchClubMutation } from '@/entities/Club';
import { ClubAccountsModal } from '@/features/ClubAccountsModal';
import { ClubChangeLogModal } from '@/features/ClubChangeLogModal';
import { ServersHistoryModal } from '@/features/ServersHistoryModal';
import { ClubTimeModal } from '@/features/ClubTimeModal';
import { phoneNumberFormatForInput } from '@/shared/lib/utils/phoneNumberFormat';
import { BlockReasonModal } from '@/features/BlockReasonModal';
import { getClubDisplayName, isActiveAccount } from '@/shared/lib/utils/accountPresentation';

export const ClubTable = () => {
  const dispatch = useAppDispatch();
  const { selectedArray } = useAppSelector((state) => state.clubTable);
  const { isLoading, data } = useGetClubsQuery();
  const [patchClub] = usePatchClubMutation();
  const tableRef = useRef(null);

  const [logModalInfo, setLogModalInfo] = useState<{ id: number; name: string } | null>(null);
  const [serversModalInfo, setServersModalInfo] = useState<{ id: number; name: string } | null>(null);
  const [timeModalInfo, setTimeModalInfo] = useState<{ id: number; name: string; readOnly: boolean } | null>(null);

  const onSelect = useCallback(
    (data: ClubTableItem[]) => {
      dispatch(setSelectedArray(data));
    },
    [dispatch],
  );

  const handleOpenLog = useCallback((clubId: number, clubName: string) => {
    setLogModalInfo({ id: clubId, name: clubName });
  }, []);

  const handleCloseLog = useCallback(() => {
    setLogModalInfo(null);
  }, []);

  const handleOpenServers = useCallback((clubId: number, clubName: string) => {
    setServersModalInfo({ id: clubId, name: clubName });
  }, []);

  const handleCloseServers = useCallback(() => {
    setServersModalInfo(null);
  }, []);

  const handleOpenTime = useCallback(
    (clubId: number, clubName: string, readOnly = false) => {
      const lifecycleReadOnly = !isActiveAccount(data?.items.find((item) => item.id === clubId)?.accountStatus);
      setTimeModalInfo({ id: clubId, name: clubName, readOnly: readOnly || lifecycleReadOnly });
    },
    [data],
  );

  const handleCloseTime = useCallback(() => {
    setTimeModalInfo(null);
  }, []);

  const handleSave = useCallback(
    async (id: number, field: keyof ClubTableItem, value: string | null, blockReason?: string | null) => {
      if (!isActiveAccount(data?.items.find((item) => item.id === id)?.accountStatus)) return;

      const patchData: any = {};

      if (field === 'loginPrefix') {
        const raw = (value ?? '').toString();
        const normalized = raw
          .replace(/[^a-zA-Z]/g, '')
          .toUpperCase()
          .slice(0, 3);
        patchData.loginPrefix = normalized;
      } else if (field === 'name') {
        patchData.name = value ?? '';
      } else if (field === 'region') {
        patchData.region = value ?? null;
      } else if (field === 'lastName') {
        patchData.lastName = value ?? null;
      } else if (field === 'firstName') {
        patchData.firstName = value ?? null;
      } else if (field === 'patronymic') {
        patchData.patronymic = value ?? null;
      } else if (field === 'contactPhone') {
        patchData.contactPhone = value ?? null;
      } else if (field === 'legalName') {
        patchData.legalName = value ?? null;
      } else if (field === 'payerInn') {
        patchData.payerInn = value ?? null;
      } else if (field === 'birthDate') {
        patchData.birthDate = value ?? null;
      } else if (field === 'isActive') {
        patchData.isActive = value === 'true';
        if (blockReason) {
          patchData.blockReason = blockReason;
        }
      }

      await patchClub({ id, data: patchData }).unwrap();
    },
    [data, patchClub],
  );

  const [accountsModal, setAccountsModal] = useState<{ id: number; name: string; readOnly: boolean } | null>(null);

  const onOpenAccounts = useCallback(
    (id: number, name: string, readOnly = false) => {
      const lifecycleReadOnly = !isActiveAccount(data?.items.find((item) => item.id === id)?.accountStatus);
      setAccountsModal({ id, name, readOnly: readOnly || lifecycleReadOnly });
    },
    [data],
  );
  const onCloseAccounts = useCallback(() => setAccountsModal(null), []);

  const [blockTargetId, setBlockTargetId] = useState<number | null>(null);
  const [blockModalOpen, setBlockModalOpen] = useState(false);

  const onToggleActive = useCallback(
    (id: number, currentActive: boolean) => {
      if (!isActiveAccount(data?.items.find((item) => item.id === id)?.accountStatus)) {
        return;
      }
      if (currentActive) {
        setBlockTargetId(id);
        setBlockModalOpen(true);
      } else {
        void handleSave(id, 'isActive', 'true');
      }
    },
    [data, handleSave],
  );

  const handleConfirmBlock = useCallback(
    (reason: string) => {
      if (blockTargetId !== null) {
        void handleSave(blockTargetId, 'isActive', 'false', reason);
      }
    },
    [blockTargetId, handleSave],
  );

  const columns = useMemo(
    () =>
      getClubTableColumns(handleSave, onOpenAccounts, handleOpenLog, handleOpenServers, handleOpenTime, onToggleActive),
    [handleSave, onOpenAccounts, handleOpenLog, handleOpenServers, handleOpenTime, onToggleActive],
  );

  const arrayOfName = selectedArray.map((item) => getClubDisplayName(item));
  const arrayOfId = selectedArray.map((item) => item.id);
  const accountStatuses = selectedArray.map((item) => item.accountStatus);

  const formattedData: ClubTableItem[] = useMemo(() => {
    const items = data?.items ?? [];
    return items.map((item: ClubItemApi) => ({
      id: item.id,
      name: item.name,
      region: item.region ?? null,
      lastName: item.lastName ?? null,
      firstName: item.firstName ?? null,
      patronymic: item.patronymic ?? null,
      contactPhone: item.contactPhone ? phoneNumberFormatForInput(item.contactPhone) : null,
      loginPrefix: item.loginPrefix ?? null,
      legalName: item.legalName ?? null,
      payerInn: item.payerInn ?? null,
      birthDate: item.birthDate ?? null,
      adminsCount: item.adminsCount ?? null,
      playersCount: item.playersCount ?? null,
      balanceTechnocoins: item.balanceTechnocoins ?? null,
      balanceLimit: item.balanceLimit ?? null,
      unlimitedUntil: item.unlimitedUntil ?? null,
      isActive: Boolean(item.isActive),
      accountStatus: item.accountStatus,
      deletion: item.deletion ?? null,
      clubType: item.clubType,
      isChanged: Boolean(item.isChanged),
    }));
  }, [data]);

  return (
    <>
      {isLoading || !formattedData ? (
        <div className={s.loaderWrapper}>
          <Loader className={s.loader} />
        </div>
      ) : (
        <Table data={formattedData} columns={columns} onSelect={onSelect} tableRef={tableRef} />
      )}
      {!!selectedArray.length && (
        <SelectedPopup
          arrayOfName={arrayOfName}
          arrayOfId={arrayOfId}
          accountStatuses={accountStatuses}
          resetSelectedRows={tableRef?.current?.resetSelectedRows}
          mode="club"
        />
      )}
      {logModalInfo && (
        <ClubChangeLogModal clubId={logModalInfo.id} clubName={logModalInfo.name} onClose={handleCloseLog} />
      )}
      {serversModalInfo && (
        <ServersHistoryModal
          mode="club"
          clubId={serversModalInfo.id}
          clubName={serversModalInfo.name}
          onClose={handleCloseServers}
        />
      )}
      {accountsModal && (
        <ClubAccountsModal
          clubId={accountsModal.id}
          clubName={accountsModal.name}
          open={true}
          readOnly={accountsModal.readOnly}
          onClose={onCloseAccounts}
        />
      )}
      {timeModalInfo && (
        <ClubTimeModal
          clubId={timeModalInfo.id}
          clubName={timeModalInfo.name}
          open={true}
          readOnly={timeModalInfo.readOnly}
          onClose={handleCloseTime}
        />
      )}
      {blockModalOpen && (
        <BlockReasonModal
          open={blockModalOpen}
          onClose={() => setBlockModalOpen(false)}
          onConfirm={handleConfirmBlock}
        />
      )}
    </>
  );
};
